import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './Toast';
import { hashPassword, validatePasswordStrengthClient } from '../utils/crypto';
import '../css.css';

const PasswordReset = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [token, setToken] = useState({ token: '', username: '' });
    const [isValidToken, setIsValidToken] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState([]);
    
    const location = useLocation();
    const navigate = useNavigate();
    const { success, error: showError } = useToast();

    // 비밀번호 강도 계산 함수 (crypto.js 사용)
    const calculatePasswordStrength = (password) => {
        return validatePasswordStrengthClient(password);
    };

    // 강화된 비밀번호 검증 (crypto.js 사용)
    const validatePasswordStrength = (password) => {
        const result = validatePasswordStrengthClient(password);
        return result.isValid ? [] : result.feedback;
    };

    // 비밀번호 토글 함수들
    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const validateToken = useCallback(async (resetToken) => {
        try {
            const response = await fetch('/api/auth/validate-reset-token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: resetToken }),
            });

            if (response.ok) {
                const data = await response.json();
                setIsValidToken(true);
                // 사용자명을 상태에 저장 (해싱에 필요)
                if (data.username) {
                    setToken({ token: resetToken, username: data.username });
                }
            } else {
                const data = await response.json();
                showError(data.message || '토큰이 만료되었거나 유효하지 않습니다.');
            }
        } catch (error) {
            console.error('토큰 검증 오류:', error);
            showError('토큰 검증 중 오류가 발생했습니다.');
        }
    }, [showError]);

    useEffect(() => {
        // URL에서 토큰 추출
        const urlParams = new URLSearchParams(location.search);
        const resetToken = urlParams.get('token');
        
        if (resetToken) {
            // 토큰 유효성 검증 (사용자명도 함께 가져옴)
            validateToken(resetToken);
        } else {
            showError('유효하지 않은 링크입니다.');
        }
    }, [location, showError, validateToken]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            showError('비밀번호가 일치하지 않습니다.');
            return;
        }

        // 강화된 비밀번호 검증
        const passwordErrors = validatePasswordStrength(newPassword);
        if (passwordErrors.length > 0) {
            showError(passwordErrors.join(' '));
            return;
        }

        setIsLoading(true);

        // 사용자명 확인
        if (!token.username) {
            showError('사용자 정보를 찾을 수 없습니다. 다시 시도해주세요.');
            setIsLoading(false);
            return;
        }

        // 보안을 위한 비밀번호 해싱 (새로운 통일된 방식)
        const hashedPassword = hashPassword(newPassword, token.username, 'login');
        
        // 메모리에서 비밀번호 즉시 제거
        setNewPassword('');
        setConfirmPassword('');

        try {
            const response = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token.token,
                    hashedPassword: hashedPassword
                }),
            });

            const data = await response.json();

            if (response.ok) {
                success('비밀번호가 성공적으로 변경되었습니다. 로그인 페이지로 이동합니다.');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            } else {
                showError(data.message || '비밀번호 변경에 실패했습니다.');
            }
        } catch (error) {
            console.error('비밀번호 재설정 오류:', error);
            showError('비밀번호 재설정 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    return (
        <div className="password-reset-container">
            <div className="password-reset-form">
                <h2>비밀번호 재설정</h2>
                
                {!isValidToken ? (
                    <div>
                        <button 
                            onClick={handleBackToHome}
                            className="back-button"
                        >
                            홈으로 돌아가기
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="newPassword">새 비밀번호:</label>
                            <div className="password-input-container">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => {
                                        const password = e.target.value;
                                        setNewPassword(password);
                                        
                                        // 비밀번호 강도 계산
                                        if (password) {
                                            const { strength, feedback } = calculatePasswordStrength(password);
                                            setPasswordStrength(strength);
                                            setPasswordFeedback(feedback);
                                        } else {
                                            setPasswordStrength(0);
                                            setPasswordFeedback([]);
                                        }
                                    }}
                                    required
                                    minLength="8"
                                    placeholder="새 비밀번호를 입력하세요"
                                    autoComplete="new-password"
                                    spellCheck="false"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={toggleNewPasswordVisibility}
                                    aria-label={showNewPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                                >
                                    {showNewPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            
                            {/* 비밀번호 강도 표시기 */}
                            {newPassword && (
                                <div className={`password-strength-container strength-${passwordStrength}`}>
                                    <div className="password-strength-bar">
                                        <div 
                                            className={`password-strength-fill strength-${passwordStrength}`}
                                            style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                        ></div>
                                    </div>
                                    <div className="password-strength-text">
                                        {passwordStrength === 5 ? '매우 강함' :
                                         passwordStrength === 4 ? '강함' :
                                         passwordStrength === 3 ? '보통' :
                                         passwordStrength === 2 ? '약함' : '매우 약함'}
                                    </div>
                                    {passwordFeedback.length > 0 && (
                                        <div className="password-feedback">
                                            <small>필요: {passwordFeedback.join(', ')}</small>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="confirmPassword">비밀번호 확인:</label>
                            <div className="password-input-container">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength="8"
                                    placeholder="비밀번호를 다시 입력하세요"
                                    autoComplete="new-password"
                                    spellCheck="false"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={toggleConfirmPasswordVisibility}
                                    aria-label={showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.45703 12C3.73128 7.94288 7.52159 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C20.2672 16.0571 16.4769 19 11.9992 19C7.52159 19 3.73128 16.0571 2.45703 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                        
                        <div className="form-actions">
                            <button 
                                type="submit" 
                                disabled={isLoading}
                                className="submit-button"
                            >
                                {isLoading ? '변경 중...' : '비밀번호 변경'}
                            </button>
                            <button 
                                type="button"
                                onClick={handleBackToHome}
                                className="back-button"
                            >
                                홈으로 돌아가기
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default PasswordReset;
